/**
 * The instrument primitives — every gauge, meter, dial and sparkline the boards
 * are drawn with, ported from the single-file demo without changing a number.
 *
 * They are pure: a canvas, some values, and nothing else. Whatever the tile
 * means is decided by the caller; these only know how to paint it. Each one
 * sizes its own backing store through `hiCanvas`, which returns null where
 * there is no 2D context to be had, so a figure that cannot be drawn leaves an
 * empty box rather than taking the board down with it.
 *
 * GENERATED by scripts/extract-gauges.mjs from .demo-src/original.js.
 */
/* eslint-disable */
import { hiCanvas } from "../lib/canvas";
import { N } from "../lib/palette";

/*
 * Writing on a canvas inside [data-crisp] is drawn without glow. The glow suits
 * a wall board seen across a room; at a desk it smears the figures, and the
 * Instruments bench asked for them clear.
 */
const crisp = (c: HTMLCanvasElement): boolean => !!c.closest?.("[data-crisp]");
import { t } from "../../i18n";

export function drawBars(c: HTMLCanvasElement, series?: any, col?: any): void{
  const hi = hiCanvas(c, 62);
  if (!hi) return;
  const { g, w, h } = hi;
  if(!series || !series.length){
    g.fillStyle = N("#3A4C58"); g.font = "700 11px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'";
    g.fillText(t("no data"), 4, h/2); return;
  }
  const max = Math.max(1, ...series.map((s: any) => s.v));
  const bw = w/series.length;
  series.forEach((s: any, i: any) => {
    const bh = (s.v/max)*(h-16);
    const grd = g.createLinearGradient(0, h-16-bh, 0, h-16);
    grd.addColorStop(0, col); grd.addColorStop(1, col + "33");
    g.fillStyle = grd;
    g.fillRect(i*bw+1, h-16-bh, Math.max(2, bw-2), bh);
  });
  g.font = "700 9.5px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'"; g.fillStyle = N("#7E93A3");
  g.fillText(series[0].l, 1, h-4);
  g.textAlign = "right"; g.fillText(series[series.length-1].l, w-1, h-4); g.textAlign = "left";
}

export function drawDonut(c: HTMLCanvasElement, parts?: any, H?: any): void{
  const hi = hiCanvas(c, H || 62);
  if (!hi) return;
  const { g, w, h } = hi;
  const cx = w/2, cy = h/2, R = Math.min(w,h)/2 - (h > 100 ? 14 : 4);
  const tot = parts.reduce((t: any, p: any) => t+p.v, 0) || 1;
  let a = -Math.PI/2;
  for(const p of parts){
    const a2 = a + (p.v/tot)*6.2832;
    g.beginPath(); g.arc(cx, cy, R, a, a2); g.lineWidth = h > 100 ? 22 : 9;
    g.strokeStyle = p.c; g.shadowBlur = 8; g.shadowColor = p.c + "88"; g.stroke(); g.shadowBlur = 0;
    a = a2;
  }
  g.textAlign = "center";
  g.font = `700 ${h > 100 ? 30 : 13}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`;
  g.fillStyle = N("#E9F0F5"); g.fillText(parts[0].v, cx, cy + (h > 100 ? 10 : 4));
  g.textAlign = "left";
}

export function drawBarMeters(c: HTMLCanvasElement, items?: any, big?: any, H?: any): void{
  const pad = big ? 14 : 3;
  const hi = hiCanvas(c, H || (items.length*(big ? 42 : 25) + pad*2));
  if (!hi) return;
  const { g, w, h } = hi;
  const rowH = (h - pad*2)/items.length;
  const tight = !big && rowH < 20;
  const labW = big ? 146 : (tight ? Math.max(66, w*0.36) : Math.min(126, Math.max(84, w*0.28)));
  const valW = big ? 62 : (tight ? 34 : 50);
  const x0 = labW, x1 = w - valW - 4;
  const cells = big ? 30 : 16;
  items.forEach((m: any, i: any) => {
    /* with room for a reading underneath, the bar sits above the row centre */
    const roomy = !big && !tight && rowH > 26;
    const y = pad + i*rowH + rowH/2 - (roomy ? 5 : 0);
    const val = Math.max(0, Math.min(1, m.score));
    const col = val > 0.7 ? "#3FD07A" : val > 0.35 ? "#F2B03A" : "#FF4D45";
    const trackH = big ? 16 : tight ? 10 : 12;
    g.font = `700 ${big ? 15 : tight ? 10 : 13}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`;
    g.fillStyle = N("#DDE8EF");
    const lab = t(m.label || "").toUpperCase();
    const maxC = big ? 17 : Math.floor(labW/(tight ? 6.1 : 7.7));
    g.fillText(lab.length > maxC ? lab.slice(0, maxC-1) + "…" : lab, 2,
      y + (big ? -2 : tight ? 3.5 : 4));
    /* recessed housing */
    g.fillStyle = N("#0A131B");
    g.fillRect(x0 - 2, y - trackH/2 - 2, x1 - x0 + 4, trackH + 4);
    g.strokeStyle = N("#1B2731"); g.lineWidth = 1;
    g.strokeRect(x0 - 1.5, y - trackH/2 - 1.5, x1 - x0 + 3, trackH + 3);
    /* segments */
    const gap = 1.2, cw = (x1 - x0 + gap)/cells - gap;
    const lit = Math.max(1, Math.round(cells*val));
    for(let k = 0; k < cells; k++){
      const sx = x0 + k*(cw + gap);
      const on = k < lit;
      g.fillStyle = on ? col : N("#15202A");
      g.globalAlpha = on ? (k === lit-1 ? 1 : 0.8) : 1;
      if(on && k === lit-1){ g.shadowBlur = big ? 12 : 7; g.shadowColor = col; }
      g.fillRect(sx, y - trackH/2, cw, trackH);
      g.shadowBlur = 0; g.globalAlpha = 1;
    }
    /* scale ticks under the bar at 0, 50 and 100 */
    if(!tight || big){
      g.strokeStyle = N("#2B3A46"); g.lineWidth = 1;
      for(let k = 0; k <= 4; k++){
        const sx = x0 + (x1-x0)*(k/4);
        g.beginPath(); g.moveTo(sx, y + trackH/2 + 3); g.lineTo(sx, y + trackH/2 + (k%2 ? 5 : 7));
        g.stroke();
      }
    }
    g.textAlign = "right";
    g.font = `700 ${big ? 17 : tight ? 10.5 : 14.5}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`;
    g.fillStyle = col;
    g.shadowBlur = 0; /* figures are read, not glowed: a halo on a dark panel smears them */
    g.fillText(Math.round(val*100) + "%", w - 2, y + (big ? -1 : tight ? 3 : 4));
    g.shadowBlur = 0;
    g.textAlign = "left";
    if(m.window && !tight){
      g.font = `700 ${big ? 12.5 : 11}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`;
      g.fillStyle = N("#93A9B8");
      const wn = String(m.window);
      const room = Math.floor((w - labW - valW)/(big ? 6.8 : 5.6));
      g.fillText(wn.length > room ? wn.slice(0, room-1) + "…" : wn, x0, y + (big ? 19 : 19));
    }
  });
}

export function drawProgress(c: HTMLCanvasElement, items?: any, big?: any, H?: any): void{
  const pad = big ? 16 : 4;
  const hi = hiCanvas(c, H || (items.length*(big ? 44 : 25) + pad*2));
  if (!hi) return;
  const { g, w, h } = hi;
  const rowH = (h - pad*2)/items.length;
  const tight = !big && rowH < 20;
  const labW = big ? 150 : (tight ? Math.max(64, w*0.34) : Math.min(124, Math.max(84, w*0.28)));
  const valW = big ? 62 : (tight ? 36 : 54);
  const x0 = labW, x1 = w - valW - 2;
  const rr = (x: any, y: any, ww: any, hh: any, r: any) => {
    const rad = Math.min(r, hh/2, Math.max(0.1, ww/2));
    g.beginPath();
    g.moveTo(x + rad, y);
    g.lineTo(x + ww - rad, y); g.quadraticCurveTo(x + ww, y, x + ww, y + rad);
    g.lineTo(x + ww, y + hh - rad); g.quadraticCurveTo(x + ww, y + hh, x + ww - rad, y + hh);
    g.lineTo(x + rad, y + hh); g.quadraticCurveTo(x, y + hh, x, y + hh - rad);
    g.lineTo(x, y + rad); g.quadraticCurveTo(x, y, x + rad, y);
    g.closePath();
  };
  items.forEach((m: any, i: any) => {
    /* with room for a reading underneath, the bar sits above the row centre so
       the label, the bar and the reading each get their own line */
    const roomy = !big && !tight && rowH > 26;
    const y = pad + i*rowH + rowH/2 - (roomy ? 5 : 0);
    const val = Math.max(0, Math.min(1, m.score));
    const col = val > 0.7 ? "#3FD07A" : val > 0.35 ? "#F2B03A" : "#FF4D45";
    const barH = big ? 18 : tight ? 12 : 14;
    g.font = `700 ${big ? 15 : tight ? 9 : 13}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`;
    g.fillStyle = N("#DDE8EF");
    const lab = t(m.label || "").toUpperCase();
    const maxC = big ? 17 : Math.floor(labW/(tight ? 5.5 : 7.6));
    g.fillText(lab.length > maxC ? lab.slice(0, maxC-1) + "…" : lab, 2,
      y + (big ? -2 : tight ? 3 : 4));
    /* recessed track with an inner shadow line */
    rr(x0, y - barH/2, x1 - x0, barH, barH/2);
    g.fillStyle = N("#0B141C"); g.fill();
    g.strokeStyle = N("#1F2C36"); g.lineWidth = 1; g.stroke();
    g.save(); rr(x0, y - barH/2, x1 - x0, barH, barH/2); g.clip();
    g.strokeStyle = "rgba(0,0,0,.55)"; g.lineWidth = 2;
    g.beginPath(); g.moveTo(x0, y - barH/2 + 1); g.lineTo(x1, y - barH/2 + 1); g.stroke();
    g.restore();
    /* fill with a lit leading edge */
    const fw = Math.max(barH, (x1 - x0)*val);
    g.save();
    rr(x0, y - barH/2, fw, barH, barH/2); g.clip();
    const grd = g.createLinearGradient(x0, y - barH/2, x0, y + barH/2);
    grd.addColorStop(0, col); grd.addColorStop(0.5, col + "DD"); grd.addColorStop(1, col + "88");
    g.fillStyle = grd;
    g.shadowBlur = big ? 14 : 8; g.shadowColor = col;
    g.fillRect(x0, y - barH/2, fw, barH);
    g.shadowBlur = 0;
    /* gloss */
    g.fillStyle = "rgba(255,255,255,.16)";
    g.fillRect(x0, y - barH/2, fw, barH*0.32);
    /* tenth marks */
    g.globalAlpha = 0.4; g.strokeStyle = N("#06121A"); g.lineWidth = 1;
    for(let k = 1; k < 10; k++){
      const sx = x0 + (x1 - x0)*(k/10);
      g.beginPath(); g.moveTo(sx, y - barH/2); g.lineTo(sx, y + barH/2); g.stroke();
    }
    g.globalAlpha = 1;
    g.restore();
    /* leading cap */
    g.fillStyle = N("#E9F0F5"); g.globalAlpha = 0.9;
    g.fillRect(x0 + fw - 2, y - barH/2, 2, barH);
    g.globalAlpha = 1;
    g.textAlign = "right";
    g.font = `700 ${big ? 17 : tight ? 10.5 : 14.5}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`;
    g.fillStyle = col;
    g.shadowBlur = 0; /* figures are read, not glowed: a halo on a dark panel smears them */
    g.fillText(Math.round(val*100) + "%", w - 2, y + (big ? -1 : tight ? 3 : 4));
    g.shadowBlur = 0; g.textAlign = "left";
    if(m.window && (big || !tight)){
      g.font = `700 ${big ? 12.5 : 11}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`;
      g.fillStyle = N("#93A9B8");
      const wn = String(m.window);
      const room = Math.floor((w - labW - valW)/(big ? 6.8 : 5.6));
      g.fillText(wn.length > room ? wn.slice(0, room-1) + "…" : wn, x0, y + (big ? 17 : 13));
    }
  });
}

export function drawVBars(c: HTMLCanvasElement, items?: any, H?: any): void{
  const hi = hiCanvas(c, H || 64);
  if (!hi) return;
  const { g, w, h } = hi;
  const n = items.length, colW = w/n;
  const big = h >= 100;
  const topPad = big ? 22 : 12, botPad = big ? 42 : 22;
  const y1 = topPad, y0 = h - botPad;
  const cells = big ? 22 : 10;                       /* segmented column, LED style */
  const gap = big ? 2 : 1.4;
  const cellH = (y0 - y1 + gap)/cells - gap;
  items.forEach((m: any, i: any) => {
    const cx = colW*i + colW/2;
    const bw = Math.min(colW - (big ? 26 : 12), big ? 32 : 18);
    const val = Math.max(0, Math.min(1, m.score));
    const col = val > 0.7 ? "#3FD07A" : val > 0.35 ? "#F2B03A" : "#FF4D45";
    const lit = Math.max(1, Math.round(cells*val));
    /* housing */
    g.fillStyle = N("#0A131B");
    g.fillRect(cx - bw/2 - 2, y1 - 2, bw + 4, y0 - y1 + 4);
    g.strokeStyle = N("#1B2731"); g.lineWidth = 1;
    g.strokeRect(cx - bw/2 - 1.5, y1 - 1.5, bw + 3, y0 - y1 + 3);
    /* scale ticks down the left of each column */
    g.strokeStyle = N("#25333E");
    for(let k = 0; k <= 4; k++){
      const ty = y0 - (y0-y1)*(k/4);
      g.beginPath();
      g.moveTo(cx - bw/2 - (k % 2 ? 3 : 5), ty); g.lineTo(cx - bw/2 - 1, ty); g.stroke();
    }
    /* threshold marks at the caution and go boundaries */
    [[0.35,"#F2B03A"],[0.7,"#3FD07A"]].forEach(([t, tc]: any[]) => {
      const ty = y0 - (y0-y1)*t;
      g.strokeStyle = tc; g.globalAlpha = 0.5; g.lineWidth = 1;
      g.beginPath(); g.moveTo(cx + bw/2 + 1, ty); g.lineTo(cx + bw/2 + 4, ty); g.stroke();
      g.globalAlpha = 1;
    });
    for(let k = 0; k < cells; k++){
      const cy = y0 - (k+1)*(cellH + gap) + gap;
      const on = k < lit;
      g.fillStyle = on ? col : N("#15202A");
      g.globalAlpha = on ? (k === lit-1 ? 1 : 0.82) : 1;
      if(on && k === lit-1){ g.shadowBlur = big ? 14 : 8; g.shadowColor = col; }
      g.fillRect(cx - bw/2, cy, bw, cellH);
      g.shadowBlur = 0; g.globalAlpha = 1;
      if(!on){ g.strokeStyle = N("#111C24"); g.lineWidth = 0.5;
        g.strokeRect(cx - bw/2 + 0.25, cy + 0.25, bw - 0.5, cellH - 0.5); }
    }
    g.textAlign = "center";
    g.font = `700 ${big ? 16 : 11}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`; g.fillStyle = col;
    g.shadowBlur = crisp(c) ? 0 : big ? 10 : 5; g.shadowColor = col + "99";
    g.fillText(Math.round(val*100) + "%", cx, y1 - (big ? 7 : 4));
    g.shadowBlur = 0;
    /* a hairline under the columns, then the caption well clear of it */
    g.strokeStyle = N("#1B2731"); g.lineWidth = 1;
    g.beginPath(); g.moveTo(cx - bw/2 - 3, y0 + 3.5); g.lineTo(cx + bw/2 + 3, y0 + 3.5); g.stroke();
    g.font = `700 ${big ? 13 : 9.5}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`; g.fillStyle = N("#C4D3DE");
    const lb = t(m.label || "").toUpperCase();
    /* a character of this monospace is ~7.9px at 13px and ~5.8px at 9.5px; a label may not reach its neighbour's column */
    const maxC = Math.floor((colW - 8)/(big ? 7.9 : 5.8));
    g.fillText(lb.length > maxC ? lb.slice(0, maxC-1) + "…" : lb, cx, h - (big ? 22 : 4));
    if(big && m.window){
      g.font = "700 10px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'"; g.fillStyle = N("#7E93A3");
      const wn = String(m.window), room = Math.floor((colW - 8)/6.1);
      g.fillText(wn.length > room ? wn.slice(0, room-1) + "…" : wn, cx, h - 6);
    }
    g.textAlign = "left";
  });
}

/**
 * An enlarged dial's pointer: a short needle across the ring only, so the
 * middle of the dial is left clear for the figure. A needle from the centre,
 * scaled up, ran through the reading.
 */
function pointer(g: CanvasRenderingContext2D, cx: number, cy: number, R: number, na: number, z: number, nc: string): void {
  const r0 = R - 9*z, r1 = R + 5*z, half = 2.2*z;
  g.beginPath();
  g.moveTo(cx + Math.cos(na)*r1, cy + Math.sin(na)*r1);
  g.lineTo(cx + Math.cos(na)*r0 + Math.cos(na+Math.PI/2)*half, cy + Math.sin(na)*r0 + Math.sin(na+Math.PI/2)*half);
  g.lineTo(cx + Math.cos(na)*r0 + Math.cos(na-Math.PI/2)*half, cy + Math.sin(na)*r0 + Math.sin(na-Math.PI/2)*half);
  g.closePath();
  g.fillStyle = N("#E9F0F5"); g.fill();
  g.strokeStyle = nc; g.lineWidth = 1.2; g.stroke();
}

export function drawGaugeGrid(c: HTMLCanvasElement, slots?: any, colsIn?: any, zoom?: any): void{
  /* zoom enlarges the dials where a desk has the room (the Instruments bench); the boards leave it at 1 */
  const z = zoom || 1;
  const cols = colsIn || (slots.length > 4 ? 3 : 2), rows = Math.ceil(slots.length/cols);
  const hi = hiCanvas(c, rows*62*z + 4 + (z > 1 ? 8 : 0));
  if (!hi) return;
  const { g, w, h } = hi;
  const cw = w/cols, ch = (h - (z > 1 ? 8 : 0))/rows;
  slots.forEach((s2: any, i: any) => {
    const col = i % cols, row = (i/cols)|0;
    const cx = cw*col + cw/2, cy = ch*row + ch - 16*z, R = Math.min(cw/2 - 10, ch/2 - 6, 22*z);
    const a0 = Math.PI, a1 = Math.PI*2;
    const band = (from: any, to: any, alpha: any) => {
      for(let k = from; k < to; k++){
        const t0 = a0 + (a1-a0)*(k/40), t1 = a0 + (a1-a0)*((k+1)/40), f = k/39;
        const cc = f < 0.5
          ? `rgb(255,${Math.round(77+f*2*99)},${Math.round(69-f*2*11)})`
          : `rgb(${Math.round(242-(f-0.5)*2*179)},${Math.round(176+(f-0.5)*2*32)},${Math.round(58+(f-0.5)*2*64)})`;
        g.beginPath(); g.arc(cx, cy, R, t0, t1+0.012);
        g.lineWidth = 6*z; g.strokeStyle = cc; g.globalAlpha = alpha; g.stroke(); g.globalAlpha = 1;
      }
    };
    band(0, 40, 0.24);
    const val = Math.max(0, Math.min(1, s2.score));
    band(0, Math.max(1, Math.round(40*val)), 1);
    const na = a0 + (a1-a0)*Math.max(0.01, val);
    const nc = val > 0.7 ? "#3FD07A" : val > 0.35 ? "#F2B03A" : "#FF4D45";
    if (z > 1) {
      pointer(g, cx, cy, R, na, z, nc);
    } else {
      g.beginPath();
      g.moveTo(cx + Math.cos(na+Math.PI/2)*2.5, cy + Math.sin(na+Math.PI/2)*2.5);
      g.lineTo(cx + Math.cos(na)*(R-2), cy + Math.sin(na)*(R-2));
      g.lineTo(cx + Math.cos(na-Math.PI/2)*2.5, cy + Math.sin(na-Math.PI/2)*2.5);
      g.closePath(); g.fillStyle = N("#E9F0F5");
      g.shadowBlur = 7; g.shadowColor = nc; g.fill(); g.shadowBlur = 0;
      g.beginPath(); g.arc(cx, cy, 3.5, 0, 6.284);
      g.fillStyle = N("#0B1620"); g.fill(); g.strokeStyle = nc; g.lineWidth = 1.4; g.stroke();
    }
    g.textAlign = "center";
    g.font = `700 ${12.5*z}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`; g.fillStyle = nc;
    g.fillText(Math.round(val*100) + "%", cx, z > 1 ? cy - 3*z : cy - 7);
    /* the caption may not reach the next dial: its length follows the room, not a fixed count */
    const fit = (px: number, fixed: number) => z === 1 ? fixed : Math.max(6, Math.floor((cw - 8)/(px*0.61)));
    g.font = `700 ${9*Math.min(z, 1.35)}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`; g.fillStyle = N("#C4D3DE");
    const lb = t(s2.label || "").toUpperCase(), lm = fit(9*Math.min(z, 1.35), 13);
    g.fillText(lb.length > lm ? lb.slice(0,lm-1) + "…" : lb, cx, ch*row + ch - 7*Math.min(z, 1.35) - (z > 1 ? 3 : 0));
    g.font = `700 ${7.5*Math.min(z, 1.35)}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`; g.fillStyle = N("#7E93A3");
    const wn = String(s2.window || ""), wm = fit(7.5*Math.min(z, 1.35), 20);
    g.fillText(wn.length > wm ? wn.slice(0,wm-1) + "…" : wn, cx, ch*row + ch);
    g.textAlign = "left";
  });
}

export function drawTriGauge(c: HTMLCanvasElement, slots?: any, zoom?: any): void{
  const z = zoom || 1;
  const hi = hiCanvas(c, 68*z);
  if (!hi) return;
  const { g, w, h } = hi;
  const n = slots.length, cw = w/n;
  slots.forEach((s2: any, i: any) => {
    const cx = cw*i + cw/2, cy = h - 27*Math.min(z, 1.3), R = Math.min(cw/2 - 8, 22*z);
    const a0 = Math.PI, a1 = Math.PI*2;
    /* the scale itself runs red to green, so the needle position is the message */
    for(let k = 0; k < 40; k++){
      const t0 = a0 + (a1-a0)*(k/40), t1 = a0 + (a1-a0)*((k+1)/40);
      const f = k/39;
      const col = f < 0.5
        ? `rgb(${255},${Math.round(77 + f*2*(176-77))},${Math.round(69 + f*2*(58-69))})`
        : `rgb(${Math.round(242 - (f-0.5)*2*(242-63))},${Math.round(176 + (f-0.5)*2*(208-176))},${Math.round(58 + (f-0.5)*2*(122-58))})`;
      g.beginPath(); g.arc(cx, cy, R, t0, t1 + 0.012);
      g.lineWidth = 7*z; g.strokeStyle = col; g.globalAlpha = 0.30; g.stroke();
      g.globalAlpha = 1;
    }
    /* filled portion up to the value */
    const val = Math.max(0, Math.min(1, s2.score));
    for(let k = 0; k < Math.max(1, Math.round(40*val)); k++){
      const t0 = a0 + (a1-a0)*(k/40), t1 = a0 + (a1-a0)*((k+1)/40);
      const f = k/39;
      const col = f < 0.5
        ? `rgb(${255},${Math.round(77 + f*2*(176-77))},${Math.round(69 + f*2*(58-69))})`
        : `rgb(${Math.round(242 - (f-0.5)*2*(242-63))},${Math.round(176 + (f-0.5)*2*(208-176))},${Math.round(58 + (f-0.5)*2*(122-58))})`;
      g.beginPath(); g.arc(cx, cy, R, t0, t1 + 0.012);
      g.lineWidth = 7*z; g.strokeStyle = col; g.stroke();
    }
    /* needle */
    const na = a0 + (a1-a0)*Math.max(0.01, val);
    const nc = val > 0.7 ? "#3FD07A" : val > 0.35 ? "#F2B03A" : "#FF4D45";
    if (z > 1) {
      pointer(g, cx, cy, R, na, z, nc);
    } else {
      g.beginPath();
      g.moveTo(cx + Math.cos(na+Math.PI/2)*3, cy + Math.sin(na+Math.PI/2)*3);
      g.lineTo(cx + Math.cos(na)*(R-2), cy + Math.sin(na)*(R-2));
      g.lineTo(cx + Math.cos(na-Math.PI/2)*3, cy + Math.sin(na-Math.PI/2)*3);
      g.closePath();
      g.fillStyle = N("#E9F0F5"); g.shadowBlur = 8; g.shadowColor = nc; g.fill(); g.shadowBlur = 0;
      g.beginPath(); g.arc(cx, cy, 4, 0, 6.2832);
      g.fillStyle = N("#0B1620"); g.fill(); g.strokeStyle = nc; g.lineWidth = 1.6; g.stroke();
    }
    /* labels */
    g.textAlign = "center";
    g.font = `700 ${12.5*z}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`; g.fillStyle = nc;
    g.fillText(Math.round(val*100) + "%", cx, z > 1 ? cy - 3*z : cy - 7);
    /* hairline under the dial, caption clear beneath it */
    g.strokeStyle = N("#1B2731"); g.lineWidth = 1;
    g.beginPath(); g.moveTo(cx - R - 2, cy + 5.5*z); g.lineTo(cx + R + 2, cy + 5.5*z); g.stroke();
    /* one caption only on the tile — the reading is already on the tile's own
       sub-line, and crowding it in here is what made the text sit on the dial */
    const lf = 10.5*Math.min(z, 1.3);
    g.font = `700 ${lf}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`; g.fillStyle = N("#C4D3DE");
    const lb = t(s2.label || "").toUpperCase(), lm = z === 1 ? 13 : Math.max(6, Math.floor((cw - 8)/(lf*0.61)));
    g.fillText(lb.length > lm ? lb.slice(0,lm-1) + "…" : lb, cx, h - 5);
    g.textAlign = "left";
  });
}

export function drawTrend(c: HTMLCanvasElement, series?: any, col?: any, H?: any, labels?: any): void{
  const hi = hiCanvas(c, H || 34);
  if (!hi) return;
  const { g, w, h } = hi;
  if(!series || series.length < 2){
    g.fillStyle = N("#3A4C58"); g.font = "700 9px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'";
    g.fillText(t("no history yet"), 3, h/2 + 3); return;
  }
  const pad = 3, n = series.length;
  const X = (i: any) => pad + (w - pad*2)*(i/(n-1));
  const Y = (v: any) => h - pad - (h - pad*2)*Math.max(0, Math.min(1, v));
  /* threshold bands so the line is read against the same limits as the meters */
  [[0.7, "#3FD07A"], [0.35, "#F2B03A"]].forEach(([v, cc]: any[]) => {
    g.strokeStyle = cc; g.globalAlpha = 0.18; g.setLineDash([3,3]); g.lineWidth = 1;
    g.beginPath(); g.moveTo(pad, Y(v)); g.lineTo(w - pad, Y(v)); g.stroke();
    g.setLineDash([]); g.globalAlpha = 1;
  });
  const grd = g.createLinearGradient(0, 0, 0, h);
  grd.addColorStop(0, col + "55"); grd.addColorStop(1, col + "00");
  g.beginPath(); g.moveTo(X(0), h - pad);
  series.forEach((v: any, i: any) => g.lineTo(X(i), Y(v)));
  g.lineTo(X(n-1), h - pad); g.closePath(); g.fillStyle = grd; g.fill();
  g.beginPath();
  series.forEach((v: any, i: any) => i ? g.lineTo(X(i), Y(v)) : g.moveTo(X(i), Y(v)));
  g.strokeStyle = col; g.lineWidth = 1.8; g.lineJoin = "round";
  g.shadowBlur = 8; g.shadowColor = col + "AA"; g.stroke(); g.shadowBlur = 0;
  g.beginPath(); g.arc(X(n-1), Y(series[n-1]), 2.6, 0, 6.284);
  g.fillStyle = N("#E9F0F5"); g.fill();
  if(labels){
    g.font = "700 8px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'"; g.fillStyle = N("#6E8496");
    g.fillText(labels[0], 2, h - 1);
    g.textAlign = "right"; g.fillText(labels[1], w - 2, h - 1); g.textAlign = "left";
  }
}

export function drawCounters(c: HTMLCanvasElement, st?: any, H?: any): void{
  const pts = st.points.slice(0, 4);
  const hi = hiCanvas(c, H || 76);
  if (!hi) return;
  const { g, w, h } = hi;
  if(!pts.length){
    g.fillStyle = N("#3A4C58"); g.font = "700 11px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'";
    g.fillText(t("AWAITING COUNTERS"), 6, h/2); return;
  }
  const big = h >= 140;
  const rowH = h/pts.length;
  pts.forEach((p: any, i: any) => {
    const y = i*rowH + rowH/2;
    const over = st.target && p.wait_min != null && p.wait_min > st.target;
    const col = over ? (p.wait_min > st.target*2 ? "#FF4D45" : "#F2B03A") : "#3FD07A";
    /* name */
    g.font = `700 ${big ? 12 : 8.5}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`; g.fillStyle = N("#9DB2C0");
    const nm = (p.name || p.id || "").toUpperCase();
    const maxC = big ? 22 : 13;
    g.fillText(nm.length > maxC ? nm.slice(0, maxC-1) + "…" : nm, 3, y - (big ? 5 : 3));
    /* counter digits */
    const shown = p.proj ?? p.people ?? 0;
    g.font = `700 ${big ? 26 : 15}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`;
    g.fillStyle = col;
    g.shadowBlur = big ? 12 : 7; g.shadowColor = col + "AA";
    g.fillText(String(shown).padStart(4, "0"), 3, y + (big ? 20 : 10));
    g.shadowBlur = 0;
    /* wait and freshness on the right */
    g.textAlign = "right";
    g.font = `700 ${big ? 15 : 10}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`; g.fillStyle = col;
    g.fillText(p.wait_min != null ? p.wait_min + " MIN" : "—", w - 3, y + (big ? 4 : 1));
    g.font = `700 ${big ? 10.5 : 7.5}px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'`; g.fillStyle = N("#6E8496");
    g.fillText(p.proj != null ? t("PROJ · {s}s", { s: p.ageS }) : `${p.ageS}s`, w - 3, y + (big ? 20 : 11));
    g.textAlign = "left";
    if(i < pts.length - 1){
      g.strokeStyle = N("#16222C"); g.lineWidth = 1;
      g.beginPath(); g.moveTo(0, (i+1)*rowH - 0.5); g.lineTo(w, (i+1)*rowH - 0.5); g.stroke();
    }
  });
}

export function drawSpark(c: HTMLCanvasElement, series?: any, cols?: any): void{
  const hi = hiCanvas(c, 92);
  if (!hi) return;
  const { g, w, h } = hi;
  const n = series[0].length;
  const max = Math.max(1, ...series.flat());
  const pad = {l:6,r:6,t:10,b:16};
  const X = (i: any) => pad.l + (i/(n-1))*(w-pad.l-pad.r);
  const Y = (v: any) => h-pad.b - (v/max)*(h-pad.t-pad.b);
  series.forEach((s: any, si: any) => {
    const grd = g.createLinearGradient(0, pad.t, 0, h-pad.b);
    grd.addColorStop(0, cols[si] + "55"); grd.addColorStop(1, cols[si] + "00");
    g.beginPath(); g.moveTo(X(0), Y(s[0]));
    s.forEach((v: any, i: any) => g.lineTo(X(i), Y(v)));
    g.lineTo(X(n-1), h-pad.b); g.lineTo(X(0), h-pad.b); g.closePath();
    g.fillStyle = grd; g.fill();
    g.beginPath(); s.forEach((v: any, i: any) => i ? g.lineTo(X(i), Y(v)) : g.moveTo(X(i), Y(v)));
    g.lineWidth = 2; g.strokeStyle = cols[si];
    g.shadowBlur = 8; g.shadowColor = cols[si] + "99"; g.stroke(); g.shadowBlur = 0;
    const last = s[n-1];
    g.beginPath(); g.arc(X(n-1), Y(last), 3.2, 0, 6.284); g.fillStyle = cols[si]; g.fill();
  });
  g.font = "700 10px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'"; g.fillStyle = N("#7E93A3");
  g.fillText(t("12 WEEKS"), pad.l, h-4);
  g.textAlign = "right"; g.fillText(t("PEAK {n}", { n: max }), w-pad.r, h-4); g.textAlign = "left";
}
